import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Loader2, CreditCard, Zap, History } from 'lucide-react';
import { motion } from 'framer-motion';
import { useGetBillingBalanceQuery, useCreateCheckoutSessionMutation, useCreatePortalSessionMutation, useSyncCheckoutSessionMutation } from '../../api/billingApi';
import { CurrentPlanBanner } from './components/CurrentPlanBanner';
import { PlansSection } from '@/features/admin/components/billing/PlansSection';
import { TopUpCards } from './components/TopUpCards';
import { BillingHistory } from './components/BillingHistory';

type Tab = 'plans' | 'topups' | 'history';

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'plans', label: 'Plans', icon: <CreditCard size={15} /> },
  { id: 'topups', label: 'Top-Ups', icon: <Zap size={15} /> },
  { id: 'history', label: 'Credit History', icon: <History size={15} /> },
];

import { CurrentPlanBannerSkeleton } from '@/features/admin/components/loaders/BillingSkeletons';

export function Billing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSyncing, setIsSyncing] = useState(false);

  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isOrgAdmin = adminUser.role === 'Admin' || adminUser.role === 'SuperAdmin' || adminUser.orgRole === 'admin';

  const [activeTab, setActiveTab] = useState<Tab>(isOrgAdmin ? 'plans' : 'topups');

  const { data: balanceData, isLoading: isBalanceLoading } = useGetBillingBalanceQuery();
  const [createCheckout, { isLoading: isCheckoutLoading }] = useCreateCheckoutSessionMutation();
  const [syncCheckout] = useSyncCheckoutSessionMutation();
  const [createPortal, { isLoading: isPortalLoading }] = useCreatePortalSessionMutation();

  const syncInitiated = useRef(false);

  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');

    if (checkoutStatus === 'success' && sessionId) {
      if (syncInitiated.current) return;
      syncInitiated.current = true;

      setIsSyncing(true);
      // Sync checkout manually to bypass webhook issues in local dev
      syncCheckout({ session_id: sessionId })
        .unwrap()
        .then(() => {
          Swal.fire({
            icon: 'success',
            title: 'Payment Successful',
            text: 'Your account has been upgraded successfully!',
            background: 'var(--admin-card-bg)',
            color: 'var(--admin-card-text)',
            confirmButtonColor: '#3E8FA6',
          });
        })
        .catch((err) => {
          console.error('Failed to sync checkout:', err);
          Swal.fire({
            icon: 'error',
            title: 'Sync Failed',
            text: 'Failed to instantly verify payment. If your payment was successful, your account will be upgraded shortly once our system processes the webhook.',
            background: 'var(--admin-card-bg)',
            color: 'var(--admin-card-text)',
            confirmButtonColor: '#3E8FA6',
          });
        })
        .finally(() => {
          setIsSyncing(false);
          searchParams.delete('checkout');
          searchParams.delete('session_id');
          setSearchParams(searchParams);
        });
    } else if (checkoutStatus === 'cancelled') {
      Swal.fire({
        icon: 'info',
        title: 'Checkout Cancelled',
        text: 'Your payment process was cancelled.',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)',
        confirmButtonColor: '#3E8FA6',
      });
      searchParams.delete('checkout');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, syncCheckout]);

  const summary = balanceData?.data;

  const handleSelectPlan = async (priceId: string) => {
    if (!priceId) return;
    try {
      const result = await createCheckout({ price_id: priceId });
      console.log("Checkout result:", result);
      
      if ('data' in result) {
        if (result.data?.success === false) {
          Swal.fire({
            icon: 'error',
            title: 'Checkout Error',
            text: result.data.message || 'Failed to initialize checkout. Please try again.',
            background: 'var(--admin-card-bg)',
            color: 'var(--admin-card-text)',
            confirmButtonColor: '#3E8FA6',
          });
          return;
        }
        if (result.data?.data?.url) {
          window.location.href = result.data.data.url;
        }
      } else if ('error' in result) {
        const errObj = result.error as any;
        const errMsg = errObj?.data?.message || errObj?.message || 'Failed to initialize checkout. Please try again.';
        Swal.fire({
          icon: 'error',
          title: 'Checkout Error',
          text: errMsg,
          background: 'var(--admin-card-bg)',
          color: 'var(--admin-card-text)',
          confirmButtonColor: '#3E8FA6',
        });
      }
    } catch (err: any) {
      console.error("Caught error:", err);
      Swal.fire({
        icon: 'error',
        title: 'Checkout Error',
        text: err?.data?.message || err?.message || 'An unexpected error occurred.',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)',
        confirmButtonColor: '#3E8FA6',
      });
    }
  };

  const handleManageBilling = async () => {
    try {
      const result = await createPortal();
      console.log("Portal result:", result);
      if ('data' in result) {
        if (result.data?.success === false) {
          Swal.fire({
            icon: 'error',
            title: 'Billing Error',
            text: result.data.message || 'Failed to open billing portal. Please try again.',
            background: 'var(--admin-card-bg)',
            color: 'var(--admin-card-text)',
            confirmButtonColor: '#3E8FA6',
          });
          return;
        }
        if (result.data?.data?.url) {
          window.location.href = result.data.data.url;
        }
      } else if ('error' in result) {
        const errObj = result.error as any;
        const errMsg = errObj?.data?.message || errObj?.message || 'Failed to open billing portal. Please try again.';
        Swal.fire({
          icon: 'error',
          title: 'Billing Error',
          text: errMsg,
          background: 'var(--admin-card-bg)',
          color: 'var(--admin-card-text)',
          confirmButtonColor: '#3E8FA6',
        });
      }
    } catch (err: any) {
      console.error("Caught error:", err);
      Swal.fire({
        icon: 'error',
        title: 'Billing Error',
        text: err?.data?.message || err?.message || 'An unexpected error occurred.',
        background: 'var(--admin-card-bg)',
        color: 'var(--admin-card-text)',
        confirmButtonColor: '#3E8FA6',
      });
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <h1
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            color: 'var(--admin-card-text)',
            fontSize: '37.9px',
            fontWeight: 700,
            margin: '0 0 6px',
            letterSpacing: '-0.76px',
            lineHeight: '38px',
          }}
        >
          Plans & Credits
        </h1>
        <p
          style={{
            fontFamily: "'Inter', sans-serif",
            color: '#7C8891',
            fontSize: '14px',
            margin: 0,
            fontWeight: 400,
            lineHeight: '20px',
            letterSpacing: '0',
          }}
        >
          Manage your subscription, purchase credits, and view your billing history.
        </p>
      </div>

      {/* Current Plan Banner */}
      {isBalanceLoading ? (
        <CurrentPlanBannerSkeleton />
      ) : summary ? (
        <CurrentPlanBanner
          summary={summary}
          onManageBilling={isOrgAdmin ? handleManageBilling : undefined}
          isPortalLoading={isPortalLoading}
        />
      ) : null}

      {/* Tabs */}
      <div
        style={{
          display: 'flex',
          gap: '4px',
          borderBottom: '1px solid var(--admin-card-border)',
          paddingBottom: '1px',
          flexShrink: 0,
          marginTop: '8px',
        }}
      >
        {TABS.filter(tab => isOrgAdmin || tab.id !== 'plans').map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 14px',
              border: 'none',
              background: 'none',
              fontSize: '12px',
              fontWeight: 700,
              fontFamily: "'Space Grotesk', sans-serif",
              color: activeTab === tab.id ? 'var(--admin-card-text)' : 'var(--admin-card-subtext)',
              cursor: 'pointer',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              borderRadius: '0',
            }}
          >
            {tab.icon}
            {tab.label}
            {activeTab === tab.id && (
              <motion.div
                layoutId="billingTabIndicator"
                style={{
                  position: 'absolute',
                  bottom: '-1px',
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: '#38bdf8',
                }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div
        style={{
          padding: '24px',
          borderRadius: '0',
          border: '1px solid var(--admin-card-border)',
          backgroundColor: 'var(--admin-card-bg)',
          flex: 1,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {activeTab === 'plans' && (
          <PlansSection />
        )}
        {activeTab === 'topups' && (
          <TopUpCards onSelectTopUp={handleSelectPlan} isLoading={isCheckoutLoading} />
        )}
        {activeTab === 'history' && <BillingHistory />}
      </div>

      {/* Full-screen Loading Overlay for Checkout Sync */}
      {isSyncing && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(4px)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#F6F9FB'
        }}>
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
            <Loader2 size={48} style={{ color: '#38bdf8' }} />
          </motion.div>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", marginTop: '24px', marginBottom: '8px', fontSize: '24px' }}>
            Verifying Payment...
          </h2>
          <p style={{ fontFamily: "'Inter', sans-serif", color: '#94a3b8', fontSize: '14px', maxWidth: '300px', textAlign: 'center', lineHeight: '1.5' }}>
            Please do not close or refresh this page. We are securely syncing your transaction with Stripe.
          </p>
        </div>
      )}
    </div>
  );
}
